<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Multiselect</h1>
    <p class="doc__lead">
      Select de selección múltiple con búsqueda interna, chips por opción seleccionada,
      botón "Seleccionar todo / Limpiar todo" y badge contador cuando hay más opciones
      de las visibles. El panel no se cierra al seleccionar — solo con click fuera o
      <code>Esc</code>. Borde normal <code>#dce0e8</code>, foco <code>#4169e2</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section
      title="Normal · Obligatorio"
      description="Estado por defecto con campo requerido."
    >
      <ui-multiselect
        [block]="true"
        label="Amenidades"
        [options]="amenities"
        [required]="true"
      />
    </app-example-section>

    <app-example-section
      title="Normal · Opcional"
      description="Muestra 'Opcional' a la derecha del label."
    >
      <ui-multiselect
        [block]="true"
        label="Recámaras"
        [options]="rooms"
        [required]="false"
        [showOptionalHint]="true"
      />
    </app-example-section>

    <app-example-section
      title="Con selección activa"
      description="Chips en el trigger. Badge +N cuando supera maxChipsVisible (3)."
    >
      <ui-multiselect
        [block]="true"
        label="Amenidades"
        [options]="amenities"
        [(selected)]="selectedAmenities"
        [required]="true"
      />
      @if (selectedAmenities().length > 0) {
        <p class="doc-value-preview">
          {{ selectedAmenities().length }} seleccionado{{ selectedAmenities().length !== 1 ? 's' : '' }}:
          {{ selectedAmenities().map(o => o.label).join(', ') }}
        </p>
      }
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo. showMessage controla visibilidad del mensaje."
    >
      <div class="doc-example-stack">
        <ui-multiselect
          [block]="true"
          label="Amenidades"
          [options]="amenities"
          [required]="true"
          [invalid]="isInvalid()"
          message="Selecciona al menos una amenidad."
          [showMessage]="showErrorMsg()"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Sin interacción; fondo atenuado."
    >
      <ui-multiselect
        [block]="true"
        label="Amenidades"
        [options]="amenities"
        [disabled]="true"
      />
    </app-example-section>

    <app-example-section
      title="Opciones deshabilitadas"
      description="Opciones individuales marcadas como disabled no se pueden seleccionar."
    >
      <ui-multiselect
        [block]="true"
        label="Amenidades"
        [options]="amenities"
        [showOptionalHint]="false"
      />
    </app-example-section>
  </div>

  <!-- ── Variantes ──────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Variantes pre-configuradas</h2>
    <p class="doc-block__p">
      Átomos derivados de <code>ui-multiselect</code> con opciones fijas y configuración compacta
      (<code>[showLabel]="false"</code>, <code>[showOptionalHint]="false"</code>).
      Listos para usar en el toolbar del <code>ui-data-table</code> vía el slot
      <code>data-toolbar-selects</code>.
    </p>

    <div class="doc-grid">
      <app-example-section
        title="ui-year-multiselect"
        description="Selección múltiple de año. Opciones: 2024, 2025, 2026. Ancho fijo 130 px."
      >
        <ui-year-multiselect [(selected)]="selectedYears" />
        @if (selectedYears().length > 0) {
          <p class="doc-value-preview">
            {{ selectedYears().length }} seleccionado{{ selectedYears().length !== 1 ? 's' : '' }}:
            {{ selectedYears().map(o => o.label).join(', ') }}
          </p>
        }
      </app-example-section>

      <app-example-section
        title="ui-month-multiselect"
        description="Selección múltiple de mes. Opciones: Enero – Diciembre. Ancho fijo 160 px."
      >
        <ui-month-multiselect [(selected)]="selectedMonths" />
        @if (selectedMonths().length > 0) {
          <p class="doc-value-preview">
            {{ selectedMonths().length }} seleccionado{{ selectedMonths().length !== 1 ? 's' : '' }}:
            {{ selectedMonths().map(o => o.label).join(', ') }}
          </p>
        }
      </app-example-section>
    </div>

    <p class="doc-block__p">Uso en <code>ui-data-table</code> (slot <code>data-toolbar-selects</code>):</p>
    <pre class="doc-block__pre" role="code"><code>selectedYears  = signal&lt;UiSelectOption[]&gt;([]);
selectedMonths = signal&lt;UiSelectOption[]&gt;([]);

&lt;ui-data-table [columns]="cols" [rows]="rows"&gt;
  &lt;ui-year-multiselect  data-toolbar-selects [(selected)]="selectedYears"  /&gt;
  &lt;ui-month-multiselect data-toolbar-selects [(selected)]="selectedMonths" /&gt;
&lt;/ui-data-table&gt;</code></pre>
  </section>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>amenities: UiSelectOption[] = [
  &#123; id: 'alberca', label: 'Alberca' &#125;,
  &#123; id: 'gym', label: 'Gimnasio' &#125;,
];
selected = signal&lt;UiSelectOption[]&gt;([]);

&lt;ui-multiselect
  label="Amenidades"
  [required]="true"
  [options]="amenities"
  [(selected)]="selected"
/&gt;</code></pre>

    <p class="doc-block__p">Extrayendo solo los IDs al enviar el formulario:</p>
    <pre class="doc-block__pre" role="code"><code>onSubmit(): void &#123;
  const ids = this.selected().map(o => o.id);
  // ['alberca', 'gym']
&#125;</code></pre>

    <p class="doc-block__p">Limitar los chips visibles en triggers compactos:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-multiselect
  label="Amenidades"
  [options]="amenities"
  [maxChipsVisible]="2"
/&gt;
&lt;!-- Con 5 selecciones muestra: chip chip +3 --&gt;</code></pre>
  </section>

  <!-- ── Inputs ──────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td><code>''</code></td>
          <td>Etiqueta visible sobre el campo.</td>
        </tr>
        <tr>
          <td><code>options</code></td>
          <td><code>UiSelectOption[]</code></td>
          <td><code>[]</code></td>
          <td>Lista de opciones disponibles.</td>
        </tr>
        <tr>
          <td><code>selected</code></td>
          <td><code>UiSelectOption[]</code> (model)</td>
          <td><code>[]</code></td>
          <td>Opciones activas. Soporta <code>[(selected)]</code>.</td>
        </tr>
        <tr>
          <td><code>placeholder</code></td>
          <td><code>string</code></td>
          <td><code>'Selecciona opciones'</code></td>
          <td>Texto cuando no hay selección.</td>
        </tr>
        <tr>
          <td><code>maxChipsVisible</code></td>
          <td><code>number</code></td>
          <td><code>3</code></td>
          <td>Chips máximos en el trigger. El excedente muestra un badge <code>+N</code>.</td>
        </tr>
        <tr>
          <td><code>searchPlaceholder</code></td>
          <td><code>string</code></td>
          <td><code>'Buscar...'</code></td>
          <td>Placeholder del input de búsqueda.</td>
        </tr>
        <tr>
          <td><code>required</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Badge "Obligatorio" y <code>aria-required</code>.</td>
        </tr>
        <tr>
          <td><code>showOptionalHint</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Badge "Opcional" cuando no es requerido.</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Deshabilita el componente completo.</td>
        </tr>
        <tr>
          <td><code>invalid</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Borde rojo y <code>aria-invalid</code>.</td>
        </tr>
        <tr>
          <td><code>message</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Texto del mensaje de error.</td>
        </tr>
        <tr>
          <td><code>showMessage</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Controla si el mensaje se renderiza.</td>
        </tr>
        <tr>
          <td><code>showLabel</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Oculta el bloque de label + badge cuando es <code>false</code>. Útil en contextos compactos como filtros en <code>ui-title-bar</code>.</td>
        </tr>
        <tr>
          <td><code>block</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td><code>display: block</code> en el host.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Teclado / Accesibilidad ───────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Teclado y accesibilidad</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Tecla</th>
          <th>Comportamiento</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><kbd>↓</kbd> / <kbd>↑</kbd></td>
          <td>Abre el panel si está cerrado; navega entre opciones cuando está abierto.</td>
        </tr>
        <tr>
          <td><kbd>Enter</kbd></td>
          <td>Abre el panel si está cerrado; hace toggle de la opción resaltada si está abierto (el panel <strong>no</strong> se cierra).</td>
        </tr>
        <tr>
          <td><kbd>Esc</kbd></td>
          <td>Cierra el panel y devuelve el foco al trigger.</td>
        </tr>
        <tr>
          <td><kbd>Tab</kbd></td>
          <td>Cierra el panel y mueve el foco al siguiente elemento de la página.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-block__p">
      Implementa el patrón ARIA <em>Listbox multiselectable</em>:
      <code>aria-multiselectable="true"</code>, <code>aria-haspopup="listbox"</code>,
      <code>aria-expanded</code>, <code>aria-controls</code>, <code>aria-activedescendant</code>
      y <code>role="option"</code> con <code>aria-selected</code> en cada ítem.
      El input de búsqueda lleva <code>role="combobox"</code>.
    </p>
  </section>

  <!-- ── Outputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>selectedChange</code></td>
          <td><code>UiSelectOption[]</code></td>
          <td>Emite el array actualizado tras cada cambio de selección.</td>
        </tr>
        <tr>
          <td><code>opened</code></td>
          <td><code>void</code></td>
          <td>Emite cuando el panel se abre.</td>
        </tr>
        <tr>
          <td><code>closed</code></td>
          <td><code>void</code></td>
          <td>Emite cuando el panel se cierra.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Usa <code>id</code> único y estable en cada opción; nunca el índice del array.</li>
      <li>Guarda el array <code>UiSelectOption[]</code> completo en el modelo y extrae solo los <code>id</code> o <code>value</code> al enviar el formulario.</li>
      <li>Ajusta <code>[maxChipsVisible]</code> según el espacio disponible; en grids compactos usa <code>1</code> o <code>2</code>.</li>
      <li>Activa <code>[invalid]</code> solo tras blur o submit, no en tiempo real.</li>
      <li>Para listas de más de 50 ítems, filtra en el backend conectando <code>(selectedChange)</code> a una llamada HTTP.</li>
    </ul>
  </section>
</article>
